import { ViewColumnAlignment, ViewPinnedDefinition } from '@msdk/definitions'
import { COLORS, MsdkStyledComponent, styled, TableCell, TableCellProps } from '@msdk/ui'

export type StyledTableCellProps = Omit<
  TableCellProps & {
    $width?: string
    $first?: boolean
    $action?: boolean
    $isExtra?: boolean
    $pinned?: ViewPinnedDefinition
    $visible?: boolean
    $idTaggingEnabled?: boolean
    $alignment?: ViewColumnAlignment
  },
  'ref'
>

function isSticky(props: StyledTableCellProps) {
  return props.$first && props.$pinned === ViewPinnedDefinition.Left
}

export const StyledTableCell: MsdkStyledComponent<StyledTableCellProps> = styled(TableCell, {
  shouldForwardProp: (prop) =>
    prop !== '$width' &&
    prop !== '$first' &&
    prop !== '$action' &&
    prop !== '$isExtra' &&
    prop !== '$pinned' &&
    prop !== '$visible' &&
    prop !== '$idTaggingEnabled' &&
    prop !== '$alignment',
})<{
  $width?: string
  $first?: boolean
  $action?: boolean
  $isExtra?: boolean
  $pinned?: ViewPinnedDefinition
  $visible?: boolean
  $idTaggingEnabled?: boolean
  $alignment?: ViewColumnAlignment
}>`
  box-sizing: border-box;
  padding: 8px;
  min-height: 52px;
  flex: ${(props) => (props.$width || props.$idTaggingEnabled ? 'unset' : '1')};
  min-width: ${(props) => (props.$width ? props.$width : 'unset')};
  width: ${(props) => (props.$width ? props.$width : 'unset')};
  max-width: ${(props) => (props.$width ? props.$width : 'unset')};
  position: ${(props) => (isSticky(props) ? 'sticky' : 'relative')};
  left: ${(props) => (isSticky(props) ? '0' : 'auto')};
  z-index: ${(props) => (isSticky(props) ? '2' : '0')};
  ${(props) => (isSticky(props) ? `border-right: 2px solid ${COLORS.divider};` : '')}
  ${(props) => (isSticky(props) ? `box-shadow: 3px 0 5px -2px rgba(0, 0, 0, 0.2);` : '')}
  background-color: ${(props) => (isSticky(props) ? COLORS.white.main : props.$isExtra ? COLORS.divider : 'inherit')};
  display: ${(props) => (props.$visible ? 'block' : 'none')};
  align-content: center;
  justify-items: ${(props) =>
    props.$alignment === ViewColumnAlignment.Center
      ? 'center'
      : props.$alignment === ViewColumnAlignment.Right
      ? 'right'
      : 'left'};
  word-break: break-word;
`

type StyledTableHeaderCellProps = StyledTableCellProps & {
  $showButtons?: boolean
}

export const StyledTableHeaderCell: MsdkStyledComponent<StyledTableHeaderCellProps> = styled(StyledTableCell, {
  shouldForwardProp: (prop) => prop !== '$showButtons',
})<{
  $showButtons?: boolean
}>`
  min-height: 56px;
  .sort-button,
  .more-button,
  .column-resizer {
    display: ${(props) => (props.$showButtons ? 'flex' : 'none')};
  }

  .more-button--backround {
    background-color: ${(props) => (props.$isExtra ? COLORS.divider : 'white')};
  }

  .sort-button.sort-button--active {
    display: flex;
  }

  &:hover {
    .sort-button,
    .more-button,
    .column-resizer {
      display: flex;
    }
  }

  ${(props) => !props.$idTaggingEnabled && 'overflow: hidden;'}
  ${(props) => !props.$idTaggingEnabled && 'white-space: nowrap;'}
  ${(props) => !props.$idTaggingEnabled && 'text-overflow: ellipsis;'}
  ${(props) => props.$showButtons && 'padding-right: 32px;'};

  &:hover {
    ${(props) => !props.$idTaggingEnabled && 'padding-right: 32px;'}
  }
`

export type ActionsTableCellProps = StyledTableCellProps & {
  shouldShow: boolean
}

export const CheckboxTableCell: MsdkStyledComponent<StyledTableCellProps> = styled(StyledTableCell, {})`
  display: table-cell;
  padding: 0 8px;
  width: 48px;
  &:first-of-type {
    padding-left: 8px;
  }
  flex: unset !important;
`

export const ActionsTableCell: MsdkStyledComponent<ActionsTableCellProps> = styled(StyledTableCell, {
  shouldForwardProp: (prop) => prop !== 'shouldShow',
})<{
  shouldShow: boolean
}>`
  ${(props) => (props.styleMode === 'Compact' ? 'padding-right: 0;' : '')}
  width: ${(props) => (props.styleMode === 'Compact' ? '48' : '56')}px;
  min-width: ${(props) => (props.styleMode === 'Compact' ? '48' : '56')}px;
  max-width: ${(props) => (props.styleMode === 'Compact' ? '48' : '56')}px;
  display: block;
  overflow: hidden;
  ${(props) => (!props.shouldShow ? ' > * { display: none; }' : '')}
  flex: unset !important;
  text-align: right;
  padding: 0 !important;
`

export const EmptyTableCell: MsdkStyledComponent<StyledTableCellProps> = styled(StyledTableCell, {})`
  display: table-cell;
  padding: 0 !important;
  overflow: hidden;
  /* background: lime; */
  flex: 1;
`

export const DetailViewTableCell: MsdkStyledComponent<
  Omit<TableCellProps, 'ref'> & React.RefAttributes<unknown>
> = styled(TableCell)`
  width: 52px;
  text-align: center;
  && {
    padding: 0;
  }
`
